import {
  type DeclencheurPerso,
  erreurDeclencheur,
  EVENEMENTS_PERSO,
  type EvenementPerso,
  nouveauDeclencheur,
} from '@imp/shared/declencheurs-perso';
import { messageErreur } from '@imp/shared/message-erreur';
import { segmentsSms } from '@imp/shared/sms';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { RelancesEmailCard } from '@/modules/messagerie/RelancesEmailCard';
import { getSmsSettings, saveSmsSettings, SMS_SETTINGS_DEFAULT, type SmsSettings } from '@/modules/messagerie/api';

const JOURS = [
  { v: 1, l: 'Lun' },
  { v: 2, l: 'Mar' },
  { v: 3, l: 'Mer' },
  { v: 4, l: 'Jeu' },
  { v: 5, l: 'Ven' },
  { v: 6, l: 'Sam' },
  { v: 0, l: 'Dim' },
];

const VARS: Record<string, string> = {
  devis: '{prenom} {numero} {montant}',
  facture: '{prenom} {numero} {montant}',
  rdv: '{prenom} {date} {ville}',
  avis: '{prenom} {lien}',
};

function palierStr(p?: number[]): string {
  return (p ?? []).join(', ');
}
function parsePaliers(s: string): number[] {
  return s
    .split(/[,\s]+/)
    .map((x) => parseInt(x, 10))
    .filter((n) => Number.isFinite(n) && n > 0);
}

export function RelancesSettings() {
  const queryClient = useQueryClient();
  const settingsQ = useQuery({ queryKey: ['sms-settings'], queryFn: getSmsSettings });
  const [s, setS] = useState<SmsSettings>(SMS_SETTINGS_DEFAULT);

  useEffect(() => {
    if (settingsQ.data) setS(settingsQ.data);
  }, [settingsQ.data]);

  const save = useMutation({
    mutationFn: async () => {
      const probleme = s.perso.map(erreurDeclencheur).find((e) => e !== null);
      if (probleme) throw new Error(probleme);
      await saveSmsSettings(s);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sms-settings'] });
    },
  });

  const setDecl = (k: keyof SmsSettings['declencheurs'], patch: Partial<SmsSettings['declencheurs']['devis']>) => {
    setS((prev) => ({ ...prev, declencheurs: { ...prev.declencheurs, [k]: { ...prev.declencheurs[k], ...patch } } }));
  };
  const setPerso = (id: string, patch: Partial<DeclencheurPerso>) => {
    setS((prev) => ({ ...prev, perso: prev.perso.map((d) => (d.id === id ? { ...d, ...patch } : d)) }));
  };
  const creerPerso = () => {
    const id = crypto.randomUUID().replace(/-/g, '').slice(0, 10);
    setS((prev) => ({ ...prev, perso: [...prev.perso, nouveauDeclencheur(id)] }));
  };
  const supprimerPerso = (id: string) => {
    setS((prev) => ({ ...prev, perso: prev.perso.filter((d) => d.id !== id) }));
  };
  const toggleJour = (v: number) => {
    setS((prev) => ({
      ...prev,
      joursOuvres: prev.joursOuvres.includes(v)
        ? prev.joursOuvres.filter((x) => x !== v)
        : [...prev.joursOuvres, v].sort(),
    }));
  };

  if (settingsQ.isLoading) return <p className="text-muted-foreground">Chargement des réglages…</p>;

  return (
    <div className="space-y-6">
      {/* Les e-mails d'abord : Brevo, depuis contact@imptoiture.com. */}
      <RelancesEmailCard />

      {/* Interrupteur maître */}
      <Card>
        <CardHeader>
          <CardTitle>Relances automatiques</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <input
              id="sms-actif"
              type="checkbox"
              checked={s.actif}
              onChange={(e) => {
                setS((p) => ({ ...p, actif: e.target.checked }));
              }}
              className="mt-1 h-4 w-4"
            />
            <label htmlFor="sms-actif">
              <span className="font-medium">Activer l&apos;envoi automatique des relances SMS</span>
              <span className="block text-sm text-muted-foreground">
                À n&apos;activer qu&apos;une fois l&apos;opérateur SMS branché (numéro + clé) et vérifié. Tant que
                c&apos;est décoché, aucun SMS automatique n&apos;est envoyé — la messagerie manuelle reste disponible.
              </span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Déclencheurs */}
      <Card>
        <CardHeader>
          <CardTitle>Déclencheurs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DeclLine
            titre="Devis sans réponse"
            desc="Relance X jours après l'envoi d'un devis resté sans réponse."
            actif={s.declencheurs.devis.actif}
            onActif={(v) => {
              setDecl('devis', { actif: v });
            }}
            paliers={palierStr(s.declencheurs.devis.paliers)}
            onPaliers={(v) => {
              setDecl('devis', { paliers: parsePaliers(v) });
            }}
          />
          <DeclLine
            titre="Facture impayée"
            desc="Relance X jours après l'échéance de paiement dépassée."
            actif={s.declencheurs.facture.actif}
            onActif={(v) => {
              setDecl('facture', { actif: v });
            }}
            paliers={palierStr(s.declencheurs.facture.paliers)}
            onPaliers={(v) => {
              setDecl('facture', { paliers: parsePaliers(v) });
            }}
          />
          <DeclLine
            titre="Rappel de RDV / intervention"
            desc="SMS la veille d'un chantier planifié."
            actif={s.declencheurs.rdv.actif}
            onActif={(v) => {
              setDecl('rdv', { actif: v });
            }}
          />
          <DeclLine
            titre="Demande d'avis Google"
            desc="SMS après un chantier terminé récemment (avec le lien d'avis)."
            actif={s.declencheurs.avis.actif}
            onActif={(v) => {
              setDecl('avis', { actif: v });
            }}
          />
        </CardContent>
      </Card>

      {/* Déclencheurs créés par l'utilisateur */}
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle>Mes déclencheurs</CardTitle>
          <Button type="button" size="sm" onClick={creerPerso}>
            <Plus className="h-4 w-4" /> Créer un déclencheur
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {s.perso.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Un SMS envoyé tout seul après un événement : par exemple, le lendemain de l&apos;envoi
              d&apos;un devis, « L&apos;avez-vous bien reçu ? ». Cliquez sur « Créer un déclencheur ».
            </p>
          ) : null}
          {s.perso.map((d) => (
            <DeclencheurPersoBloc
              key={d.id}
              d={d}
              onChange={(patch) => {
                setPerso(d.id, patch);
              }}
              onSupprimer={() => {
                supprimerPerso(d.id);
              }}
            />
          ))}
          {s.perso.length > 0 ? (
            <p className="text-xs text-muted-foreground">
              Chaque déclencheur écrit une seule fois par devis, facture ou chantier, dans le
              créneau d&apos;envoi ci-dessous, et seulement si l&apos;envoi automatique est activé.
              Un déclencheur créé aujourd&apos;hui n&apos;écrit pas aux clients des mois passés.
              N&apos;oubliez pas « Enregistrer les réglages ».
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* Créneau d'envoi */}
      <Card>
        <CardHeader>
          <CardTitle>Créneau d&apos;envoi</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="hd">De (heure)</Label>
              <Input
                id="hd"
                type="number"
                min={0}
                max={23}
                value={s.heureDebut}
                onChange={(e) => {
                  setS((p) => ({ ...p, heureDebut: parseInt(e.target.value, 10) || 0 }));
                }}
                className="w-24"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hf">À (heure)</Label>
              <Input
                id="hf"
                type="number"
                min={1}
                max={24}
                value={s.heureFin}
                onChange={(e) => {
                  setS((p) => ({ ...p, heureFin: parseInt(e.target.value, 10) || 0 }));
                }}
                className="w-24"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Jours</Label>
              <div className="flex flex-wrap gap-1.5">
                {JOURS.map((j) => (
                  <button
                    key={j.v}
                    type="button"
                    onClick={() => {
                      toggleJour(j.v);
                    }}
                    className={`h-12 rounded-md border px-3 text-sm font-medium lg:h-11 ${
                      s.joursOuvres.includes(j.v)
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-muted-foreground'
                    }`}
                  >
                    {j.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Fuseau Europe/Paris. Aucun SMS automatique n&apos;est envoyé en dehors de ce créneau.
          </p>
        </CardContent>
      </Card>

      {/* Lien avis + Templates */}
      <Card>
        <CardHeader>
          <CardTitle>Messages types</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="lien">Lien d&apos;avis Google</Label>
            <Input
              id="lien"
              value={s.lienAvis}
              onChange={(e) => {
                setS((p) => ({ ...p, lienAvis: e.target.value }));
              }}
            />
          </div>
          {(['devis', 'facture', 'rdv', 'avis'] as const).map((k) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={`tpl-${k}`} className="capitalize">
                {k}
              </Label>
              <Textarea
                id={`tpl-${k}`}
                value={s.templates[k]}
                onChange={(e) => {
                  setS((p) => ({ ...p, templates: { ...p.templates, [k]: e.target.value } }));
                }}
                rows={3}
              />
              <p className="text-[11px] text-muted-foreground">Variables : {VARS[k]}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button
          type="button"
          onClick={() => {
            save.mutate();
          }}
          disabled={save.isPending}
        >
          <Save className="h-4 w-4" />
          {save.isPending ? 'Enregistrement…' : 'Enregistrer les réglages'}
        </Button>
        {save.isSuccess ? <span className="text-sm text-green-600">Enregistré ✓</span> : null}
        {save.isError ? (
          <span className="text-sm text-destructive">{messageErreur(save.error)}</span>
        ) : null}
      </div>
    </div>
  );
}

function DeclencheurPersoBloc(props: {
  d: DeclencheurPerso;
  onChange: (patch: Partial<DeclencheurPerso>) => void;
  onSupprimer: () => void;
}) {
  const { d } = props;
  const ev = EVENEMENTS_PERSO[d.evenement];
  const [confirmer, setConfirmer] = useState(false);
  const segments = segmentsSms(d.message);
  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-2 pb-2">
          <input
            id={`perso-actif-${d.id}`}
            type="checkbox"
            checked={d.actif}
            onChange={(e) => {
              props.onChange({ actif: e.target.checked });
            }}
            className="h-4 w-4"
          />
          <Label htmlFor={`perso-actif-${d.id}`}>Actif</Label>
        </div>
        <div className="min-w-[200px] flex-1 space-y-1.5">
          <Label htmlFor={`perso-nom-${d.id}`}>Nom</Label>
          <Input
            id={`perso-nom-${d.id}`}
            value={d.nom}
            onChange={(e) => {
              props.onChange({ nom: e.target.value });
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`perso-ev-${d.id}`}>Quand</Label>
          <Select
            id={`perso-ev-${d.id}`}
            value={d.evenement}
            onChange={(e) => {
              const evenement = e.target.value as EvenementPerso;
              // Le message type suit l'événement tant qu'on ne l'a pas réécrit.
              const modeleActuel = d.message === ev.modele;
              props.onChange({
                evenement,
                ...(modeleActuel ? { message: EVENEMENTS_PERSO[evenement].modele } : {}),
              });
            }}
          >
            {(Object.keys(EVENEMENTS_PERSO) as EvenementPerso[]).map((k) => (
              <option key={k} value={k}>
                {EVENEMENTS_PERSO[k].label}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`perso-delai-${d.id}`}>Délai</Label>
          <div className="flex items-center gap-2">
            <Input
              id={`perso-delai-${d.id}`}
              type="number"
              min={0}
              max={365}
              value={d.delai}
              onChange={(e) => {
                props.onChange({ delai: parseInt(e.target.value, 10) || 0 });
              }}
              className="w-20"
            />
            <span className="text-sm text-muted-foreground">
              {d.delai === 0
                ? ev.sens === 'apres'
                  ? 'jour, dès que possible'
                  : 'jour, le jour même'
                : `jour${d.delai > 1 ? 's' : ''} ${ev.sens === 'apres' ? 'après' : 'avant'}`}
            </span>
          </div>
        </div>
        {confirmer ? (
          <div className="flex items-center gap-2 pb-1">
            <Button type="button" size="sm" variant="destructive" onClick={props.onSupprimer}>
              Supprimer
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setConfirmer(false);
              }}
            >
              Garder
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={`Supprimer le déclencheur « ${d.nom} »`}
            onClick={() => {
              setConfirmer(true);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`perso-msg-${d.id}`}>Message</Label>
        <Textarea
          id={`perso-msg-${d.id}`}
          value={d.message}
          onChange={(e) => {
            props.onChange({ message: e.target.value });
          }}
          rows={3}
        />
        <p className="text-[11px] text-muted-foreground">
          Variables : {ev.variables} · environ {segments} SMS par envoi
        </p>
      </div>
    </div>
  );
}

function DeclLine(props: {
  titre: string;
  desc: string;
  actif: boolean;
  onActif: (v: boolean) => void;
  paliers?: string;
  onPaliers?: (v: string) => void;
}) {
  const cbId = `decl-${props.titre.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div className="flex flex-col gap-2 border-b pb-4 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <input
          id={cbId}
          type="checkbox"
          checked={props.actif}
          onChange={(e) => {
            props.onActif(e.target.checked);
          }}
          className="mt-1 h-4 w-4"
        />
        <label htmlFor={cbId}>
          <span className="font-medium">{props.titre}</span>
          <span className="block text-sm text-muted-foreground">{props.desc}</span>
        </label>
      </div>
      {props.onPaliers ? (
        <div className="flex items-center gap-2 pl-7 sm:pl-0">
          <Label htmlFor={`pal-${props.titre}`} className="text-xs text-muted-foreground">
            Jours
          </Label>
          <Input
            id={`pal-${props.titre}`}
            value={props.paliers}
            onChange={(e) => {
              props.onPaliers?.(e.target.value);
            }}
            placeholder="7, 14"
            className="w-28"
          />
        </div>
      ) : null}
    </div>
  );
}
